import { useState } from "react";

/**
 * Manages drag-to-reorder state for a list rendered with native HTML5 DnD.
 *
 * Usage:
 *   const { dragIndex, dragOverIndex, handlers, reorder } = useDraggableList(items);
 *
 *   // Spread handlers onto each draggable element:
 *   <div draggable {...handlers(idx)}>…</div>
 *
 *   // `onDrop` receives the reordered array — call your mutation there:
 *   const { handlers } = useDraggableList(items, (reordered, movedItem, newIdx) => {
 *     myMutation({ id: movedItem.id, priority: newIdx + 1 });
 *   });
 */
export function useDraggableList<T>(
  items: T[],
  onReorder?: (reordered: T[], movedItem: T, newIndex: number) => void,
) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handlers = (index: number) => ({
    onDragStart: () => setDragIndex(index),

    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      if (index !== dragIndex) setDragOverIndex(index);
    },

    onDrop: (e: React.DragEvent) => {
      e.preventDefault();

      if (dragIndex === null || dragIndex === index) {
        setDragIndex(null);
        setDragOverIndex(null);

        return;
      }

      const reordered = [...items];
      const [moved] = reordered.splice(dragIndex, 1);
      reordered.splice(index, 0, moved);

      setDragIndex(null);
      setDragOverIndex(null);

      onReorder?.(reordered, moved, index);
    },

    onDragEnd: () => {
      setDragIndex(null);
      setDragOverIndex(null);
    },
  });

  const isDragging = (index: number) => dragIndex === index;
  const isDragOver = (index: number) =>
    dragOverIndex === index && dragIndex !== index;

  return { dragIndex, dragOverIndex, handlers, isDragging, isDragOver };
}
